import { queryOptions, useQueries, useQueryClient, useSuspenseQuery, type QueryClient } from '@tanstack/react-query';
import { hydraUiApi } from '@/services/api';
import type { QueryConfig } from '../shared/react-query-types';
import { requireWorkspaceId } from '../shared/workspace';
import { SQL_DATABASE_TYPES, type SqlDatabaseType } from '../sql-import-database/entity';
import { sqlImportKeys, type SqlImport, type SqlImportWithDatabase } from './entity';
import { parseSqlImportReply, populateSqlImportList, populateSqlImportReply } from './populate';

/**
 * The imports reading from one database type, joined to their databases. The
 * endpoint requires the `?database=` selector, so the whole workspace is six
 * reads (see `useSqlImportsByDatabase`).
 *
 * @public
 */
export async function getSqlImports(
  workspaceId: number,
  database: SqlDatabaseType,
  qc: QueryClient,
): Promise<SqlImportWithDatabase[]> {
  const data = await hydraUiApi.sqlImports.list({
    params: { path: { environment_id: String(workspaceId) }, query: { database } },
  });
  return populateSqlImportList(qc, workspaceId, data);
}

/** Always fresh: an import's `active` flag and last sync move server-side between visits. */
export const getSqlImportsQueryOptions = (workspaceId: number, database: SqlDatabaseType, qc: QueryClient) =>
  queryOptions({
    queryKey: sqlImportKeys.list(workspaceId, database),
    queryFn: () => getSqlImports(workspaceId, database, qc),
    staleTime: 0,
  });

interface UseSqlImportsOptions {
  database: SqlDatabaseType;
  /**
   * Use this id instead of reading the URL. Pages that render inside a Suspense
   * boundary can mount while the URL is still mid-redirect, so a caller that
   * already resolved the workspace passes it in.
   */
  workspaceId?: number;
  queryConfig?: QueryConfig<typeof getSqlImportsQueryOptions>;
}

/** @public */
export const useSqlImports = ({ database, workspaceId: overrideWorkspaceId, queryConfig }: UseSqlImportsOptions) => {
  const workspaceId = overrideWorkspaceId ?? requireWorkspaceId();
  const qc = useQueryClient();
  return useSuspenseQuery({ ...getSqlImportsQueryOptions(workspaceId, database, qc), ...queryConfig });
};

export type SqlImportsByDatabase = Record<SqlDatabaseType, SqlImportWithDatabase[]>;

/**
 * The six per-database reads combined into a record keyed by database type.
 * Each read settles on its own: a database whose read is pending or failed
 * contributes an empty array, so one failing engine does not blank the others,
 * and `isPending` is true until every read has settled.
 *
 * Pass an explicit `workspaceId` when the caller has already resolved it (e.g.
 * a page that resolved the `/workspaces/last` sentinel from the router
 * location): `requireWorkspaceId` reads `window.location`, which lags the
 * router location during that redirect.
 */
export function useSqlImportsByDatabase(workspaceId?: number): {
  byDatabase: SqlImportsByDatabase;
  isPending: boolean;
} {
  const resolvedWorkspaceId = workspaceId ?? requireWorkspaceId();
  const qc = useQueryClient();
  const results = useQueries({
    queries: SQL_DATABASE_TYPES.map((database) => getSqlImportsQueryOptions(resolvedWorkspaceId, database, qc)),
  });

  // Index-aligned with SQL_DATABASE_TYPES; a pending or failed read reads as empty.
  const importsFor = (database: SqlDatabaseType): SqlImportWithDatabase[] =>
    results[SQL_DATABASE_TYPES.indexOf(database)]?.data ?? [];

  return {
    byDatabase: {
      mysql: importsFor('mysql'),
      postgres: importsFor('postgres'),
      snowflake: importsFor('snowflake'),
      bigquery: importsFor('bigquery'),
      redshift: importsFor('redshift'),
      sqlserver: importsFor('sqlserver'),
    },
    isPending: results.some((result) => result.isPending),
  };
}

/**
 * One import. The reply sideloads its database, which lands in the
 * `sql-import-database` cache for `getSqlImportDatabaseCache` to read.
 *
 * @public
 */
export async function getSqlImport(workspaceId: number, id: string, qc: QueryClient): Promise<SqlImport> {
  const data = await hydraUiApi.sqlImports.get({
    params: { path: { environment_id: String(workspaceId), id } },
  });
  return populateSqlImportReply(qc, workspaceId, parseSqlImportReply(data));
}

/** @public */
export const getSqlImportQueryOptions = (workspaceId: number, id: string, qc: QueryClient) =>
  queryOptions({
    queryKey: sqlImportKeys.detail(workspaceId, id),
    queryFn: () => getSqlImport(workspaceId, id, qc),
    staleTime: 0,
  });

interface UseSqlImportOptions {
  id: string;
  workspaceId?: number;
  queryConfig?: QueryConfig<typeof getSqlImportQueryOptions>;
}

/** @public */
export const useSqlImport = ({ id, workspaceId: overrideWorkspaceId, queryConfig }: UseSqlImportOptions) => {
  const workspaceId = overrideWorkspaceId ?? requireWorkspaceId();
  const qc = useQueryClient();
  return useSuspenseQuery({ ...getSqlImportQueryOptions(workspaceId, id, qc), ...queryConfig });
};
